import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Box, Card, Flex, Heading, Table, Text } from "@radix-ui/themes";
import { Users } from "lucide-react";
import { groupService } from "@/services/breeze/GroupService";
import { useAuth } from "@/providers/AuthProvider";
import { Group } from "@/types/breeze/Group";
import { SkeletonList } from "@/components/ui/Skeleton";

interface Props {
  userId: number;
}

export default function UserGroupsCard({ userId }: Props) {
  const { currentTenant } = useAuth();

  const { data: groups, isLoading, error } = useQuery<Group[]>({
    queryKey: ["user-groups", userId],
    queryFn: () => groupService.listGroupsForUser(userId),
  });

  return (
    <Card>
      <Box p="4">
        <Flex align="center" gap="2" mb="3">
          <Users size={18} />
          <Heading size="4">Group memberships</Heading>
        </Flex>

        {isLoading ? (
          <SkeletonList rows={3} />
        ) : error ? (
          <Text size="2" color="red">Failed to load groups</Text>
        ) : !groups || groups.length === 0 ? (
          <Text size="2" color="gray">This user isn't in any groups.</Text>
        ) : (
          <Table.Root variant="surface" size="1">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Description</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {groups.map((g) => (
                <Table.Row key={g.id}>
                  <Table.Cell>
                    <Link
                      to={`/org/${currentTenant?.id}/settings/groups/${g.id}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {g.name}
                    </Link>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="2" color="gray">{g.description ?? "—"}</Text>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </Box>
    </Card>
  );
}
